import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { format } from 'date-fns';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../context/BranchContext';
import { customerFeedbackAPI } from '../../services';
import PaginationComponent from '../../components/PaginationComponent';
import { toast } from 'react-toastify';
import useFilterParams from '../../hooks/useFilterParams';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';

/**
 * #112630 (BA-3) — "Phản ánh khách".
 *
 * Vì sao có màn này: phản ánh khách trước đây chỉ bay qua Telegram rồi nằm im trong DB, không
 * ai truy được món nào/ai pha, nên tình huống "mấy lần rồi" ở CN Q10 không lần ra được. Màn này
 * gộp 2 câu hỏi của quản lý vào 1 chỗ: (1) ai đang bị phản ánh lặp lại → cần đào tạo lại,
 * (2) cụ thể từng lần khách chê gì.
 *
 * Nguyên tắc: phản ánh KHÔNG suy ra được nhân viên vẫn hiện đầy đủ, ghi rõ "Không xác định" —
 * không ẩn đi, không báo lỗi, và KHÔNG gán bừa cho ai (dữ liệu này dùng để quyết định đào tạo).
 */

const CATEGORY_LABELS = {
  taste: 'Hương vị',
  service: 'Phục vụ',
  ambiance: 'Không gian',
  price: 'Giá cả',
};

const CATEGORY_OPTIONS = [
  { value: '', label: 'Tất cả hạng mục' },
  ...Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label })),
];

const RATING_OPTIONS = [
  { value: '', label: 'Tất cả điểm' },
  { value: '2', label: 'Chỉ phản ánh nặng (≤ 2 sao)' },
  { value: '3', label: 'Chưa hài lòng (≤ 3 sao)' },
];

// Giá trị này phải khớp `UNKNOWN_EMPLOYEE_FILTER` bên
// on-coffee-server/src/modules/customer-feedback/customer-feedback.service.ts.
// Dùng chuỗi riêng vì để TRỐNG đã mang nghĩa "mọi nhân viên".
const UNKNOWN_EMPLOYEE = 'unknown';

const FILTER_SPEC = {
  employee_id: { default: '' },
  category: {
    default: '',
    valid: (v) => Object.keys(CATEGORY_LABELS).includes(v),
  },
  max_rating: {
    default: '',
    valid: (v) => RATING_OPTIONS.some((o) => o.value && o.value === v),
  },
  start_date: { default: '' },
  end_date: { default: '' },
  id: { default: '' },
};

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return format(new Date(value), 'dd/MM/yyyy HH:mm');
  } catch {
    return value;
  }
};

const parseListResponse = (response) => {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) {
    return { items: payload, total: payload.length, page: 1, limit: 20, total_pages: 1 };
  }
  const items = Array.isArray(payload?.data) ? payload.data : [];
  return {
    items,
    total: payload?.total ?? items.length,
    page: payload?.page ?? 1,
    limit: payload?.limit ?? 20,
    total_pages: payload?.total_pages ?? 1,
  };
};

const parseArrayResponse = (response) => {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload?.data) ? payload.data : [];
};

const ratingBadge = (rating) => {
  if (rating <= 2) return 'danger';
  if (rating === 3) return 'warning';
  return 'success';
};

const CustomerFeedbackPage = () => {
  const { selectedBranch } = useBranch();
  const branchId = selectedBranch?.id;

  const [urlFilters, setUrlFilters, , { ignored, dismissIgnored }] =
    useFilterParams(FILTER_SPEC);
  const { employee_id: employeeFilter, category, max_rating, start_date, end_date } =
    urlFilters;
  const highlightId = urlFilters.id;

  const [rows, setRows] = useState([]);
  const [byEmployee, setByEmployee] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 20, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // Đổi bất kỳ bộ lọc nào thì về trang 1 (luật 4 của useFilterParams).
  const setFilter = (patch) => {
    setUrlFilters(patch);
    setMeta((prev) => ({ ...prev, page: 1 }));
  };

  const baseParams = useCallback(
    () => ({
      branch_id: branchId || undefined,
      category: category || undefined,
      max_rating: max_rating || undefined,
      start_date: start_date || undefined,
      end_date: end_date || undefined,
    }),
    [branchId, category, max_rating, start_date, end_date],
  );

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      // Bảng đếm cố tình KHÔNG nhận `employee_id`: chọn 1 nhân viên thì bảng đếm vẫn phải
      // hiện đủ mọi người để còn so được ai nhiều hơn ai.
      const [listRes, countRes] = await Promise.all([
        customerFeedbackAPI.getFeedbacks({
          ...baseParams(),
          employee_id: employeeFilter || undefined,
          page: meta.page,
          limit: meta.limit,
        }),
        customerFeedbackAPI.getCountByEmployee(baseParams()),
      ]);
      const { items, total, page, limit, total_pages } = parseListResponse(listRes);
      setRows(items);
      setMeta({ total, page, limit, total_pages });
      setByEmployee(parseArrayResponse(countRes));
    } catch (error) {
      console.error('Cannot load customer feedbacks', error);
      const msg = error?.message || 'Không tải được danh sách phản ánh khách';
      toast.error(msg);
      setLoadError(msg);
      setRows([]);
      setByEmployee([]);
    } finally {
      setLoading(false);
    }
  }, [baseParams, employeeFilter, meta.page, meta.limit]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handlePageChange = (page) => setMeta((prev) => ({ ...prev, page }));

  const resetFilters = () =>
    setFilter({
      employee_id: '',
      category: '',
      max_rating: '',
      start_date: '',
      end_date: '',
    });

  // Nhân viên đang được chọn — dùng để nhấn mạnh dòng trong bảng đếm.
  const isSelected = (employeeId) =>
    employeeFilter === (employeeId === null ? UNKNOWN_EMPLOYEE : employeeId);

  const toggleEmployee = (employeeId) => {
    const value = employeeId === null ? UNKNOWN_EMPLOYEE : employeeId;
    setFilter({ employee_id: employeeFilter === value ? '' : value });
  };

  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header">
          <div className="mc-page-header-title">
            <h1>Phản ánh khách</h1>
            <p className="text-muted mb-0 small">
              Phản ánh của khách kèm nhân viên đã pha món, để phát hiện ai bị phản ánh lặp lại
              và cần đào tạo lại. Bấm một dòng ở bảng bên trái để lọc theo nhân viên đó.
            </p>
          </div>
          <Button variant="primary" onClick={fetchAll} disabled={loading}>
            Làm mới
          </Button>
        </div>

        <InvalidParamsNotice ignored={ignored} onDismiss={dismissIgnored} />

        <Card className="mb-4">
          <Card.Header>
            <Row className="g-3 align-items-end">
              <Col xs={12} md={3}>
                <Form.Label className="small text-muted mb-1">Hạng mục</Form.Label>
                <Form.Select
                  value={category}
                  onChange={(e) => setFilter({ category: e.target.value })}
                >
                  {CATEGORY_OPTIONS.map((o) => (
                    <option key={o.value || 'all'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md={3}>
                <Form.Label className="small text-muted mb-1">Mức điểm</Form.Label>
                <Form.Select
                  value={max_rating}
                  onChange={(e) => setFilter({ max_rating: e.target.value })}
                >
                  {RATING_OPTIONS.map((o) => (
                    <option key={o.value || 'all'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="small text-muted mb-1">Từ ngày</Form.Label>
                <Form.Control
                  type="date"
                  value={start_date}
                  onChange={(e) => setFilter({ start_date: e.target.value })}
                />
              </Col>
              <Col xs={6} md={2}>
                <Form.Label className="small text-muted mb-1">Đến ngày</Form.Label>
                <Form.Control
                  type="date"
                  value={end_date}
                  onChange={(e) => setFilter({ end_date: e.target.value })}
                />
              </Col>
              <Col xs={12} md={2}>
                <Button variant="outline-secondary" className="w-100" onClick={resetFilters}>
                  Xoá bộ lọc
                </Button>
              </Col>
            </Row>
          </Card.Header>
        </Card>

        <Row className="g-4">
          <Col xs={12} xl={4}>
            <Card className="h-100">
              <Card.Header>
                <strong>Số lần bị phản ánh theo nhân viên</strong>
                <div className="text-muted small">
                  Theo đúng bộ lọc đang chọn. Nhân viên lặp lại nhiều lần là người cần đào tạo lại.
                </div>
              </Card.Header>
              <Card.Body className="p-0">
                {loading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" /> Đang tải...
                  </div>
                ) : byEmployee.length === 0 ? (
                  <div className="text-center text-muted py-4">Chưa có phản ánh nào.</div>
                ) : (
                  <Table hover className="mb-0">
                    <thead>
                      <tr>
                        <th>Nhân viên</th>
                        <th className="text-end">Số lần</th>
                        <th className="text-end">Điểm TB</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byEmployee.map((e) => (
                        <tr
                          key={e.employee_id ?? UNKNOWN_EMPLOYEE}
                          role="button"
                          className={isSelected(e.employee_id) ? 'table-primary' : undefined}
                          onClick={() => toggleEmployee(e.employee_id)}
                        >
                          <td>
                            {e.employee_id ? (
                              e.employee_name
                            ) : (
                              // Không quy được về ai — nói thẳng, không gộp vào một nhân viên nào.
                              <span className="text-muted fst-italic">Không xác định</span>
                            )}
                          </td>
                          <td className="text-end">
                            <Badge bg={e.feedback_count >= 3 ? 'danger' : 'secondary'}>
                              {e.feedback_count}
                            </Badge>
                          </td>
                          <td className="text-end">{e.avg_rating ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xs={12} xl={8}>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <strong>Phản ánh gần đây</strong>
                <span className="text-muted small">{meta.total} phản ánh</span>
              </Card.Header>
              <Card.Body className="p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" size="sm" /> Đang tải...
                  </div>
                ) : loadError ? (
                  <div className="text-center text-danger py-5">
                    Không tải được danh sách phản ánh khách: {loadError}
                  </div>
                ) : rows.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    Không có phản ánh nào khớp bộ lọc.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table striped hover className="mb-0">
                      <thead>
                        <tr>
                          <th>Thời gian</th>
                          <th>Chi nhánh</th>
                          <th>Điểm</th>
                          <th>Hạng mục</th>
                          <th>Món</th>
                          <th>Nhân viên pha</th>
                          <th>Nội dung</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr
                            key={r.id}
                            id={`customer-feedback-${r.id}`}
                            className={
                              highlightId && r.id === highlightId ? 'table-warning' : undefined
                            }
                          >
                            <td className="text-nowrap">{formatDateTime(r.created_at)}</td>
                            <td>
                              {r.branch_name ?? '-'}
                              {r.table_number ? (
                                <div className="text-muted small">Bàn {r.table_number}</div>
                              ) : null}
                            </td>
                            <td>
                              <Badge bg={ratingBadge(r.rating)}>{r.rating}/5</Badge>
                            </td>
                            <td>{r.category ? (CATEGORY_LABELS[r.category] ?? r.category) : '-'}</td>
                            <td>
                              {r.menu_item_name ?? (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {r.employee_name ? (
                                r.employee_name
                              ) : (
                                <span className="text-muted fst-italic">Không xác định</span>
                              )}
                              {/* Nói rõ vì sao quy được/không quy được, để người xem lại biết
                                  bản ghi này chứng minh cho ai chứ không phải đoán từ ô trống. */}
                              {r.attribution_source === 'SINGLE_SERVER' && (
                                <div className="text-muted small">Suy từ đơn (1 người pha)</div>
                              )}
                              {r.attribution_source === 'UNKNOWN' && (
                                <div className="text-muted small">
                                  Đơn nhiều người pha hoặc phản ánh cũ
                                </div>
                              )}
                            </td>
                            {/* Phản ánh thật có thể dài cả đoạn (xem dòng 20/05 trong dữ liệu
                                dump) — cắt bớt để bảng còn đọc được, giữ nguyên toàn văn ở
                                tooltip để quản lý vẫn xem đủ khi cần. */}
                            <td style={{ maxWidth: 280 }} title={r.comment || undefined}>
                              {r.comment
                                ? r.comment.length > 120
                                  ? `${r.comment.slice(0, 120)}…`
                                  : r.comment
                                : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
              {meta.total_pages > 1 && (
                <Card.Footer className="bg-white">
                  <PaginationComponent
                    totalItems={meta.total}
                    itemsPerPage={meta.limit}
                    currentPage={meta.page}
                    onPageChange={handlePageChange}
                    loading={loading}
                  />
                </Card.Footer>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </PageLayout>
  );
};

export default CustomerFeedbackPage;
